export type ProjectSection = { title: string; content: string };

export type Project = {
  id: number;
  title: string;
  description: string;
  sections?: ProjectSection[];
  category: string;
  status: string;
  tech: string[];
  github: string;
  liveUrl?: string;
  color: string;
  gradient: string;
  backgroundImage: string;
};

export type Skill = {
  name: string;
  level: string;
};

export type SkillCategory = {
  category: string;
  color: string;
  skills: Skill[];
};

export const PROJECTS: Project[] = [
  {
    id: 4,
    title: 'Forsa',
    description:
      'Forsa is a SaaS web application designed to simplify event management by connecting Organizers, Venue Owners, and Attendees in one unified platform. Built with modern technologies, Forsa focuses on performance, scalability, and user experience, enabling seamless event creation, booking, and management.',
    sections: [
      { title: 'Problem', content: 'Finding and managing events, venues, and bookings is still fragmented across multiple platforms, making the experience inefficient for attendees, organizers, and venue owners.' },
      { title: 'Solution', content: 'Developed Forsa, a full-stack event management platform that connects attendees, organizers, and venue owners in one system with event discovery, booking, venue management, notifications, and feedback features.' },
      { title: 'Result', content: 'Created a scalable multi-role platform with smart event recommendations, real-time booking workflows, and streamlined event operations that improve user engagement and event organization efficiency.' }
    ],
    category: 'Web',
    status: 'completed',
    tech: ['.NET', 'TypeScript', 'TailWind', 'SQL', 'Clean Architecture', 'REST API'],
    github: 'https://github.com/Marwan-Hussein/Forsa',
    liveUrl: 'https://forsa-app.runasp.net/',
    color: '#0063d4',
    gradient: 'from-dark-blue/40 to-dark-blue/10',
    backgroundImage: '/assets/images/ForsaLogo.png',
  },
  {
    id: 3,
    title: 'Hnefatafl',
    description:
      'Hnefatafl is an asymmetrical strategy game. One player controls the Attackers (the dark army), while the other controls the Defenders (the light army) and the King.',
    sections: [
      { title: 'Board Size', content: '9x9 grid.' },
      { title: 'Starting Position', content: 'The King starts on the central square (The Throne), surrounded by his defenders. The attackers start at the edges of the board.' }
    ],
    category: 'Desktop',
    status: 'completed',
    tech: ['Python', 'Tkinter'],
    github: 'https://github.com/Marwan-Hussein/Hnefatafl-Game/releases/',
    color: '#b60000',
    gradient: 'from-neon-amber/20 to-neon-amber/5',
    backgroundImage: '/assets/images/GameBackground.png',
  },
  {
    id: 2,
    title: 'School Task Assignment Site',
    description:
      'A simple and user-friendly school task assignment web application where Admins can assign and manage tasks for Teachers, and Teachers can view, complete, and interact with assigned tasks. Built with Django and REST API.',
    category: 'Web',
    status: 'completed',
    tech: ['Django', 'Python', 'REST API', 'HTML', 'CSS'],
    github: 'https://github.com/Ahmed-3bdellatif/Task-assignment-site',
    color: '#bf00ff',
    gradient: 'from-neon-purple/20 to-neon-purple/5',
    backgroundImage: '/assets/images/EditTaskPage.png',
  },
  {
    id: 1,
    title: 'Task Worker Matching',
    description:
      'A desktop application that helps clients find and connect with suitable workers (e.g., plumbers, carpenters, electricians) for specific tasks. Includes user registration, task management, rating and feedback mechanisms, and matching logic. Implemented with a GUI using Qt C++ and backed by an SQL database.',
    category: 'Desktop',
    status: 'completed',
    tech: ['C++', 'Qt', 'SQL'],
    github: 'https://github.com/esraa-emary/Task-Worker-Matching',
    color: '#00f5ff',
    gradient: 'from-neon-cyan/20 to-neon-cyan/5',
    backgroundImage: '/assets/images/homepage.png',
  },
];

export const SKILL_CATEGORIES: SkillCategory[] = [
  {
    category: 'Skills',
    color: '#bf00ff',
    skills: [
      { name: 'Data Structures & Algorithms', level: 'Advanced' },
      { name: 'System Analysis and Design', level: 'Proficient' },
      { name: 'Algorithms Analysis and Design', level: 'Proficient' },
      { name: 'Python', level: 'Advanced' },
      { name: 'HTML', level: 'Intermediate' },
      { name: 'CSS', level: 'Intermediate' },
      { name: 'JavaScript', level: 'Intermediate' },
      { name: 'C#', level: 'Proficient' },
      { name: 'Java', level: 'Advanced' },
      { name: 'C++', level: 'Proficient' },
      { name: 'MySQL', level: 'Advanced' },
      { name: 'SQL', level: 'Proficient' },
      { name: 'SQLite', level: 'Advanced' },
    ],
  },
  {
    category: 'Technologies',
    color: '#00f5ff',
    skills: [
      { name: 'Django', level: 'Familiar' },
      { name: 'ASP.NET', level: 'Proficient' },
      { name: 'Node.js', level: 'Intermediate' },
      { name: 'Express', level: 'Intermediate' },
    ],
  },
  {
    category: 'Tools',
    color: '#00ff88',
    skills: [
      { name: 'Git & GitHub', level: 'Advanced' },
      { name: 'Visual Studio', level: 'Proficient' },
      { name: 'Visual Studio Code', level: 'Advanced' },
      { name: 'JetBrains Products', level: 'Intermediate' },
      { name: 'Microsoft SQL Server', level: 'Advanced' },
    ],
  },
];

export const TECH_BADGES = [
  'Django', 'MySQL', 'SQL', 'SQLite', 'Python',
  'HTML', 'CSS', 'JavaScript', 'C#', 'ASP.NET',
  'C++', 'Java', 'Git & GitHub', 'Visual Studio',
  'Visual Studio Code', 'JetBrains Products',
];

// Single source of truth for portfolio counts/metrics
export const PORTFOLIO_STATS = {
  // Automatically calculated values
  projects: PROJECTS.length,
  completedProjects: PROJECTS.filter((p) => p.status === 'completed').length,
  skills: SKILL_CATEGORIES.reduce((acc, cat) => acc + cat.skills.length, 0),
  technologies: TECH_BADGES.length,
  skillAreas: SKILL_CATEGORIES.length,
  // Single-place configurable metrics
  certifications: 6,
};
